// ARC-4: world = mm, +X right, +Y down. Angles in degrees, 0° = +X, positive = clockwise on screen.
// All trig/vector math routes through this module.

export interface Vec {
  x: number;
  y: number;
}

export const EPS = 1e-6;

export function vec(x: number, y: number): Vec {
  return { x, y };
}

export function add(a: Vec, b: Vec): Vec {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function sub(a: Vec, b: Vec): Vec {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(a: Vec, s: number): Vec {
  return { x: a.x * s, y: a.y * s };
}

export function dist(a: Vec, b: Vec): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function len(a: Vec): number {
  return Math.hypot(a.x, a.y);
}

export function eq(a: Vec, b: Vec, eps = EPS): boolean {
  return Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;
}

export function lerp(a: Vec, b: Vec, t: number): Vec {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

export function normalize(a: Vec): Vec {
  const l = len(a);
  return l <= EPS ? { x: 0, y: 0 } : { x: a.x / l, y: a.y / l };
}

const DEG2RAD = Math.PI / 180;

/** Unit direction for an angle in degrees (ARC-4 convention). */
export function dir(deg: number): Vec {
  const r = deg * DEG2RAD;
  return { x: Math.cos(r), y: Math.sin(r) };
}

/** Angle of a vector in degrees, normalized to [0, 360). */
export function angleOf(a: Vec): number {
  const deg = Math.atan2(a.y, a.x) / DEG2RAD;
  return normalizeDeg(deg);
}

export function normalizeDeg(deg: number): number {
  const d = deg % 360;
  return d < 0 ? d + 360 : d;
}

/** Rotate `p` around `center` by `deg` (positive = clockwise on screen under Y-down). */
export function rotate(p: Vec, center: Vec, deg: number): Vec {
  const r = deg * DEG2RAD;
  const c = Math.cos(r);
  const s = Math.sin(r);
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  return { x: center.x + dx * c - dy * s, y: center.y + dx * s + dy * c };
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function emptyBounds(): Bounds {
  return { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
}

export function boundsValid(b: Bounds): boolean {
  return b.minX <= b.maxX && b.minY <= b.maxY;
}

export function extendBounds(b: Bounds, p: Vec): void {
  if (p.x < b.minX) b.minX = p.x;
  if (p.y < b.minY) b.minY = p.y;
  if (p.x > b.maxX) b.maxX = p.x;
  if (p.y > b.maxY) b.maxY = p.y;
}

export function unionBounds(a: Bounds, b: Bounds): Bounds {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY)
  };
}

/** EC-RND-1: clamp coordinates entering the document to ±1e9 mm. */
export const COORD_LIMIT = 1e9;

export function clampCoord(v: number): number {
  return Math.min(COORD_LIMIT, Math.max(-COORD_LIMIT, v));
}

export function isFiniteVec(p: Vec): boolean {
  return Number.isFinite(p.x) && Number.isFinite(p.y);
}
